/**
 * Vendor bills, and the three-way match.
 *
 * This is the screen where procurement becomes accounting. Everything before it
 * — the request, the order, the delivery — posts nothing. A bill posts, and the
 * match panel is what stands between a bill and the ledger: ordered, received
 * and invoiced have to agree before the amount is payable.
 *
 * The list is what needs doing; a bill opens beside it with its match, its
 * papers, its balance and the one action that moves it on. Recording a bill
 * opens its form beside the list, and the papers are attached right there.
 */

import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import Attachments from '../components/Attachments'
import BillPosition from '../components/BillPosition'
import { ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'
import SearchSelect from '../components/SearchSelect'
import { gstinState } from '../gstStates'

//: The heads a bill may be withheld under, as the year's rule pack names
//: them, with the section people say out loud. "" is the vendor's default
//: and "none" is an explicit nothing.
const TDS_HEADS: [string, string][] = [
  ['contractor', '194C — contractor'],
  ['professional_fees', '194J — professional fees'],
  ['rent', '194I — rent'],
  ['rent_by_non_audit_individual', '194IB — rent by an individual'],
  ['purchase_of_goods', '194Q — purchase of goods'],
  ['interest_other_than_securities', '194A — interest'],
]
const headLabel = (head: string | null | undefined) =>
  TDS_HEADS.find(([value]) => value === head)?.[1] ?? head ?? 'none'

interface TdsPreview {
  applies: boolean
  tds_paise: number
  rate_bp: number
  head: string | null
  reason: string
}

interface MatchLine {
  line_no: number
  description: string
  ordered: string
  received: string
  invoiced: string
  status: string
  detail: string
}

interface Invoice {
  id: string
  reference: string
  supplier_invoice_no: string
  party_id: string
  purchase_order_id: string | null
  invoice_date: string
  status: string
  match_status: string
  match_summary: string
  payable: boolean
  taxable_paise: number
  tax_paise: number
  cgst_paise: number
  sgst_paise: number
  igst_paise: number
  total_paise: number
  itc_eligible: boolean
  is_rcm: boolean
  //: As recorded: null for the vendor's default, "none" for no TDS.
  tds_head: string | null
  //: Withheld once posted; until then, what posting would withhold.
  tds_paise: number
  net_payable_paise: number
  version: number
  approval_state: string
  journal_entry_id: string | null
  match_lines: MatchLine[]
  //: Recorded from a bank payment: when it was paid, and where the supplier's
  //: invoice stands (attached, due, overdue) against its deadline.
  paid_from_bank_on: string | null
  invoice_state: string | null
  invoice_due_on: string | null
  party_name: string | null
  //: Typed on the bill, or worked out from the vendor's terms.
  due_date: string | null
  location_id: string | null
  department_id: string | null
  cost_center_id: string | null
  //: Bill-wise, posted bills only.
  paid_paise: number
  outstanding_paise: number
  days_overdue: number
  payment_status: PaymentStatus | null
  lines: InvoiceLine[]
}

type PaymentStatus = 'unpaid' | 'partially_paid' | 'paid' | 'overdue'

const PAYMENT_STATUS: Record<PaymentStatus, string> = {
  unpaid: 'Unpaid',
  partially_paid: 'Partly paid',
  paid: 'Paid',
  overdue: 'Overdue',
}

interface InvoiceLine {
  line_no: number
  description: string
  quantity: string | null
  amount_paise: number
  gl_account_id: string
  category_id: string | null
  purchase_order_line_id: string | null
  location_id: string | null
  department_id: string | null
  cost_center_id: string | null
}

interface Option {
  id: string
  code: string | null
  name: string
  note?: string | null
}

interface FormOptions {
  departments: Option[]
  locations: Option[]
  cost_centers: Option[]
  categories: Option[]
}

interface Vendor {
  id: string
  name: string
  //: The default head a bill from them is withheld under, if any.
  tds_head: string | null
  gstin: string | null
  state_code: string | null
}

interface Order {
  id: string
  reference: string
  party_id: string
  lines: { id: string; line_no: number; description: string }[]
}

interface Account {
  id: string
  code: string
  name: string
  account_type: string
  is_postable: boolean
  is_active: boolean
}

//: What a bill line may be charged to: an expense account that takes postings
//: and is still in use. Loans, capital and the like were in the list before,
//: one slip away from "Courier and postage".
const isExpenseHead = (a: Account): boolean =>
  a.account_type === 'expense' && a.is_postable && a.is_active

interface DraftLine {
  description: string
  quantity: string
  amount: string
  //: The category picks the expense head; the head stays for bills recorded
  //: before categories were offered here.
  category_id: string
  gl_account_id: string
  purchase_order_line_id: string
}

const BLANK: DraftLine = {
  description: '',
  quantity: '',
  amount: '',
  category_id: '',
  gl_account_id: '',
  purchase_order_line_id: '',
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

const today = () => new Date().toISOString().slice(0, 10)

type Tab = 'open' | 'posted' | 'all'

const rupees = (paise: number) => (paise / 100).toFixed(2)

export default function Bills() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)
  const [tab, setTab] = useState<Tab>('open')
  const [search, setSearch] = useState('')
  const [onlyUnmatched, setOnlyUnmatched] = useState(false)
  const [vendorFilter, setVendorFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | PaymentStatus>('')
  const [editing, setEditing] = useState<Invoice | null>(null)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['invoices'] })

  const invoices = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.get<Invoice[]>('/procurement/invoices'),
  })
  const vendors = useQuery({
    queryKey: ['parties'],
    queryFn: () => api.get<Vendor[]>('/parties'),
    enabled: can('party.vendor.view'),
  })
  const orders = useQuery({
    queryKey: ['orders'],
    queryFn: () => api.get<Order[]>('/procurement/orders'),
    enabled: can('proc.purchase_order.view'),
  })
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/coa/accounts'),
    enabled: can('org.coa.view'),
  })
  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
    enabled: can('spend.payment_request.view_own'),
  })

  const submit = useMutation({
    mutationFn: (id: string) => api.post(`/procurement/invoices/${id}/submit`),
    onSuccess: refresh,
  })
  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.post(`/procurement/invoices/${id}/decide?approve=${approve}`),
    onSuccess: refresh,
  })
  const post = useMutation({
    mutationFn: (id: string) => api.post(`/procurement/invoices/${id}/post`),
    onSuccess: refresh,
  })
  //: Cancel a bill not yet in the books, or -- the Finance Head -- void a
  //: posted one nothing has been paid against.
  const withdraw = useMutation({
    mutationFn: ({ id, how, reason }: { id: string; how: 'cancel' | 'void'; reason: string }) =>
      api.post(`/procurement/invoices/${id}/${how}`, { reason }),
    onSuccess: refresh,
  })

  const rows = invoices.data ?? []
  const vendorName = (id: string) => (vendors.data ?? []).find((v) => v.id === id)?.name ?? '—'
  const unmatched = (inv: Invoice) => !inv.payable && inv.match_status !== 'not_applicable'
  const shown = rows.filter(
    (inv) =>
      (tab === 'all' || (tab === 'posted' ? inv.status === 'posted' : inv.status !== 'posted')) &&
      (!onlyUnmatched || unmatched(inv)) &&
      (!vendorFilter || inv.party_id === vendorFilter) &&
      (!statusFilter || inv.payment_status === statusFilter) &&
      matches(
        search,
        inv.reference,
        inv.supplier_invoice_no,
        vendorName(inv.party_id),
        (inv.total_paise / 100).toFixed(2),
      ),
  )
  const paged = usePaged(shown, `${tab}|${search}|${onlyUnmatched}|${vendorFilter}|${statusFilter}`)

  if (invoices.isLoading) return <Loading what="Loading bills" />

  const step = (inv: Invoice) => {
    if (inv.status === 'draft')
      return can('ap.invoice.submit')
        ? { label: 'Ready to submit', tone: 'turn' }
        : { label: 'Draft', tone: 'draft' }
    if (inv.status === 'submitted')
      return can('ap.invoice.approve')
        ? { label: 'Waiting for you', tone: 'turn', detail: inv.approval_state }
        : { label: 'With a checker', tone: 'submitted', detail: inv.approval_state }
    if (inv.status === 'approved')
      return can('ap.invoice.create')
        ? { label: 'Ready to post', tone: 'turn' }
        : { label: 'Approved, not posted', tone: 'advisory' }
    if (inv.status === 'posted' && inv.payment_status)
      return {
        label: PAYMENT_STATUS[inv.payment_status],
        tone: inv.payment_status,
        detail:
          inv.payment_status === 'overdue'
            ? `${inv.days_overdue} days past due`
            : inv.payment_status === 'paid'
              ? undefined
              : `due ${inv.due_date}`,
      }
    if (inv.status === 'posted') return { label: 'In the ledger', tone: 'approved' }
    return { label: inv.status.replace(/_/g, ' '), tone: inv.status }
  }
  const stepCell = (inv: Invoice) => {
    const s = step(inv)
    return (
      <div className="next-step">
        <span className={`pill ${s.tone}`}>{s.label}</span>
        {'detail' in s && s.detail && <span className="faint">{s.detail}</span>}
      </div>
    )
  }

  const openRows = rows.filter((i) => i.status !== 'posted' && i.status !== 'cancelled')
  const posted = rows.filter((i) => i.status === 'posted')
  const outstanding = posted.reduce((t, i) => t + i.outstanding_paise, 0)
  const overdue = posted.filter((i) => i.payment_status === 'overdue')
  const mine = rows.filter((i) => step(i).tone === 'turn').length
  const notPayable = rows.filter(unmatched)
  const opened = rows.find((i) => i.id === open)

  return (
    <>
      <ErrorNote error={invoices.error} />

      <div className="bank-toolbar">
        {can('ap.invoice.create') && (
          <button className="primary" onClick={() => setRecording(true)}>
            Record a bill
          </button>
        )}
      </div>

      <div className="grid">
        <Stat label="Waiting for you" value={String(mine)} note="to submit, approve or post" />
        <Stat
          label="Not yet in the ledger"
          value={String(openRows.length)}
          note={`${formatInr(openRows.reduce((t, i) => t + i.total_paise, 0))} in bills`}
        />
        <Stat
          label="Match not clean"
          value={String(notPayable.length)}
          note="not payable until ordered, received and billed agree"
        />
        <Stat
          label="Outstanding"
          value={formatInr(outstanding)}
          note={`on ${posted.filter((i) => i.outstanding_paise > 0).length} posted bills${
            overdue.length ? ` · ${overdue.length} overdue` : ''
          }`}
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'open', label: 'To finish', count: openRows.length },
              { key: 'posted', label: 'Posted', count: posted.length },
              { key: 'all', label: 'All bills', count: rows.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Reference, supplier bill, vendor, amount"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search bills"
            />
            <select
              aria-label="Vendor"
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
            >
              <option value="">Every vendor</option>
              {(vendors.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Payment status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as '' | PaymentStatus)}
            >
              <option value="">Any payment status</option>
              {(Object.keys(PAYMENT_STATUS) as PaymentStatus[]).map((k) => (
                <option key={k} value={k}>
                  {PAYMENT_STATUS[k]}
                </option>
              ))}
            </select>
            <label className="appr-check">
              <input
                type="checkbox"
                checked={onlyUnmatched}
                onChange={(e) => setOnlyUnmatched(e.target.checked)}
              />
              only where the match is not clean
            </label>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0
                ? 'No bills recorded yet.'
                : tab === 'open' && !search && !onlyUnmatched
                  ? 'Every bill is posted.'
                  : 'No bill matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Vendor and bill</th>
                  <th>Date</th>
                  <th className="num">Total</th>
                  <th className="num">TDS</th>
                  <th className="num">Payable</th>
                  <th className="num">Paid</th>
                  <th className="num">Outstanding</th>
                  <th>Match</th>
                  <th>Next step</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((inv) => (
                  <tr key={inv.id} {...rowProps(() => setOpen(inv.id), open === inv.id)}>
                    <td>
                      <div className="strong">{vendorName(inv.party_id)}</div>
                      <div className="cell-sub">
                        <span className="mono">{inv.supplier_invoice_no}</span> ·{' '}
                        <span className="ref">{inv.reference}</span>
                      </div>
                    </td>
                    <td className="muted date">{inv.invoice_date}</td>
                    <td className="num">{formatInr(inv.total_paise)}</td>
                    <td className="num">
                      {inv.tds_paise > 0 ? (
                        <>
                          {formatInr(inv.tds_paise)}
                          <div className="cell-sub">
                            {inv.tds_head ? headLabel(inv.tds_head) : 'vendor’s default'}
                            {inv.status !== 'posted' && ' · expected'}
                          </div>
                        </>
                      ) : (
                        <span className="faint">
                          {inv.tds_head === 'none' ? 'none, as recorded' : '—'}
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <strong>{formatInr(inv.net_payable_paise)}</strong>
                    </td>
                    <td className="num">
                      {inv.status === 'posted' ? (
                        formatInr(inv.paid_paise)
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td className="num">
                      {inv.status === 'posted' ? (
                        <strong>{formatInr(inv.outstanding_paise)}</strong>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td>
                      <Pill value={inv.match_status} />
                      {unmatched(inv) && <div className="cell-sub">not payable yet</div>}
                    </td>
                    <td>{stepCell(inv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      {opened && (
        <BillPanel
          key={opened.id}
          invoice={opened}
          vendorName={vendorName(opened.party_id)}
          badge={stepCell(opened)}
          onClose={() => setOpen(null)}
          foot={
            <>
              <ErrorNote error={submit.error ?? decide.error ?? post.error ?? withdraw.error} />
              {opened.status === 'draft' && opened.paid_from_bank_on && (
                <span className="faint">
                  Recorded from a bank payment — it books from the Banking screen once its supplier
                  is approved. It needs no approval of its own.
                </span>
              )}
              {opened.status === 'draft' &&
                !opened.paid_from_bank_on &&
                can('ap.invoice.submit') && (
                  <button
                    className="primary"
                    disabled={submit.isPending}
                    onClick={() => submit.mutate(opened.id)}
                  >
                    Submit for approval
                  </button>
                )}
              {opened.status === 'submitted' && can('ap.invoice.approve') && (
                <>
                  <button
                    className="primary"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: opened.id, approve: true })}
                  >
                    Approve
                  </button>
                  <button
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: opened.id, approve: false })}
                  >
                    Reject
                  </button>
                </>
              )}
              {opened.status === 'submitted' && !can('ap.invoice.approve') && (
                <span className="faint">With a checker — nothing for you to do.</span>
              )}
              {opened.status === 'approved' && can('ap.invoice.create') && (
                <button
                  className="primary"
                  disabled={post.isPending}
                  onClick={() => post.mutate(opened.id)}
                >
                  Post to the ledger
                </button>
              )}
              {['draft', 'rejected'].includes(opened.status) &&
                !opened.paid_from_bank_on &&
                can('ap.invoice.edit') && (
                  <button onClick={() => setEditing(opened)}>Correct the bill</button>
                )}
              {!['posted', 'cancelled'].includes(opened.status) &&
                !opened.paid_from_bank_on &&
                can('ap.invoice.edit') && (
                  <ReasonButton
                    label="Cancel the bill"
                    prompt="Why is it cancelled?"
                    pending={withdraw.isPending}
                    onGo={(reason) => withdraw.mutate({ id: opened.id, how: 'cancel', reason })}
                  />
                )}
              {opened.status === 'posted' && opened.paid_paise === 0 && can('ap.invoice.void') && (
                <ReasonButton
                  label="Void the bill"
                  prompt="Why is it voided?"
                  pending={withdraw.isPending}
                  onGo={(reason) => withdraw.mutate({ id: opened.id, how: 'void', reason })}
                />
              )}
            </>
          }
        />
      )}

      {recording && (
        <SidePanel
          kicker="Vendor bill"
          title="Record a bill"
          sub="one bill is routinely two expense heads"
          wide
          onClose={() => setRecording(false)}
        >
          <NewBill
            vendors={vendors.data ?? []}
            orders={orders.data ?? []}
            accounts={(accounts.data ?? []).filter(isExpenseHead)}
            options={options.data}
            onDone={refresh}
            onFinished={() => setRecording(false)}
          />
        </SidePanel>
      )}

      {editing && (
        <SidePanel
          kicker="Vendor bill"
          title={`Correct ${editing.reference}`}
          sub="saving sends it back to draft; submit it again for approval"
          wide
          onClose={() => setEditing(null)}
        >
          <NewBill
            key={editing.id}
            editing={editing}
            vendors={vendors.data ?? []}
            orders={orders.data ?? []}
            accounts={(accounts.data ?? []).filter(isExpenseHead)}
            options={options.data}
            onDone={refresh}
            onFinished={() => setEditing(null)}
          />
        </SidePanel>
      )}
    </>
  )
}

/** A button that asks for a reason before it acts. */
function ReasonButton({
  label,
  prompt,
  pending,
  onGo,
}: {
  label: string
  prompt: string
  pending: boolean
  onGo: (reason: string) => void
}) {
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  if (!asking) return <button onClick={() => setAsking(true)}>{label}</button>
  return (
    <>
      <input
        aria-label={prompt}
        placeholder={prompt}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <button disabled={pending || !reason.trim()} onClick={() => onGo(reason.trim())}>
        {label}
      </button>
      <button className="small" onClick={() => setAsking(false)}>
        Keep it
      </button>
    </>
  )
}

/** One bill: its figures, the three-way match, its papers and its balance. */
function BillPanel({
  invoice,
  vendorName,
  badge,
  foot,
  onClose,
}: {
  invoice: Invoice
  vendorName: string
  badge: ReactNode
  foot: ReactNode
  onClose: () => void
}) {
  const { can } = useAuth()
  const [balance, setBalance] = useState(false)
  return (
    <SidePanel
      kicker="Vendor bill"
      title={vendorName}
      sub={
        <>
          Bill <span className="mono">{invoice.supplier_invoice_no}</span> of {invoice.invoice_date}{' '}
          · <span className="ref">{invoice.reference}</span>
        </>
      }
      amount={formatInr(invoice.total_paise)}
      badge={badge}
      wide
      foot={foot}
      onClose={onClose}
    >
      <section>
        <dl className="appr-facts">
          <div>
            <dt>Taxable</dt>
            <dd>{formatInr(invoice.taxable_paise)}</dd>
          </div>
          <div>
            <dt>GST</dt>
            <dd>{formatInr(invoice.tax_paise)}</dd>
          </div>
          <div>
            <dt>TDS {invoice.status === 'posted' ? 'withheld' : 'expected'}</dt>
            <dd>
              {formatInr(invoice.tds_paise)}
              {invoice.tds_paise > 0 && (
                <div className="faint">
                  {invoice.tds_head ? headLabel(invoice.tds_head) : 'vendor’s default'}
                </div>
              )}
            </dd>
          </div>
          <div>
            <dt>Payable to vendor</dt>
            <dd>
              <strong>{formatInr(invoice.net_payable_paise)}</strong>
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <Pill value={invoice.status} />
              {invoice.journal_entry_id && <div className="faint">in the ledger</div>}
            </dd>
          </div>
          <div>
            <dt>Due</dt>
            <dd>
              {invoice.due_date ?? '—'}
              {invoice.status === 'posted' && invoice.days_overdue > 0 && (
                <div className="faint">{invoice.days_overdue} days past due</div>
              )}
            </dd>
          </div>
          {invoice.status === 'posted' && invoice.payment_status && (
            <>
              <div>
                <dt>Paid</dt>
                <dd>{formatInr(invoice.paid_paise)}</dd>
              </div>
              <div>
                <dt>Outstanding</dt>
                <dd>
                  <strong>{formatInr(invoice.outstanding_paise)}</strong>
                  <div>
                    <span className={`pill ${invoice.payment_status}`}>
                      {PAYMENT_STATUS[invoice.payment_status]}
                    </span>
                  </div>
                </dd>
              </div>
            </>
          )}
          <div>
            <dt>Approval</dt>
            <dd>{invoice.approval_state}</dd>
          </div>
        </dl>
        {!invoice.itc_eligible && (
          <Note kind="info">
            Input credit not claimable, so the tax is part of what the thing cost and lands in the
            expense rather than an input-tax account.
          </Note>
        )}
        {invoice.is_rcm && (
          <Note kind="info">
            Reverse charge: the supplier is owed the taxable value only. The tax is ours to pay and
            a separate self-invoice raises it — booking it here too would double it.
          </Note>
        )}
      </section>

      <section>
        <h3>
          Three-way match{' '}
          <span className="hint">
            {invoice.purchase_order_id ? '· ordered, received, invoiced' : '· no purchase order'}
          </span>
        </h3>
        <Note kind={invoice.payable ? 'ok' : 'warn'}>{invoice.match_summary}</Note>
        {invoice.match_lines.length > 0 && (
          <div className="scroll-x">
            <table className="appr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th className="num">Ordered</th>
                  <th className="num">Received</th>
                  <th className="num">Invoiced</th>
                  <th>Finding</th>
                </tr>
              </thead>
              <tbody>
                {invoice.match_lines.map((line) => (
                  <tr key={line.line_no}>
                    <td className="muted">{line.line_no}</td>
                    <td>{line.description}</td>
                    <td className="num">{Number(line.ordered)}</td>
                    <td className="num">{Number(line.received)}</td>
                    <td className="num">{Number(line.invoiced)}</td>
                    <td>
                      <Pill value={line.status} />
                      <div className="faint">{line.detail}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3>Papers</h3>
        {invoice.paid_from_bank_on && invoice.invoice_due_on && (
          <Note
            kind={
              invoice.invoice_state === 'attached'
                ? 'ok'
                : invoice.invoice_state === 'overdue'
                  ? 'warn'
                  : 'info'
            }
          >
            Paid from the bank on {invoice.paid_from_bank_on}.{' '}
            {invoice.invoice_state === 'attached'
              ? 'The supplier’s invoice is attached.'
              : `The supplier’s invoice ${
                  invoice.invoice_state === 'overdue' ? 'was due' : 'is due'
                } by ${invoice.invoice_due_on} — until it is attached, the GST on this bill cannot be claimed.`}
          </Note>
        )}
        <Attachments
          subjectType="vendor_invoice"
          subjectId={invoice.id}
          kind="invoice"
          canAttach={can('ap.invoice.create')}
          label="Attach the supplier’s bill, a delivery note, anything else"
        />
      </section>

      {invoice.status === 'posted' && (
        <section>
          <h3>Balance</h3>
          {balance ? (
            <BillPosition invoiceId={invoice.id} onClose={() => setBalance(false)} />
          ) : (
            <button className="small" onClick={() => setBalance(true)}>
              Show what is paid and what is left
            </button>
          )}
        </section>
      )}
    </SidePanel>
  )
}

function NewBill({
  vendors,
  orders,
  accounts,
  options,
  editing,
  onDone,
  onFinished,
}: {
  vendors: Vendor[]
  orders: Order[]
  accounts: Account[]
  options?: FormOptions
  //: A draft or rejected bill being corrected, rather than a new one.
  editing?: Invoice
  onDone: () => void
  onFinished: () => void
}) {
  const [form, setForm] = useState(() => ({
    supplier_invoice_no: editing?.supplier_invoice_no ?? '',
    party_id: editing?.party_id ?? '',
    purchase_order_id: editing?.purchase_order_id ?? '',
    invoice_date: editing?.invoice_date ?? today(),
    //: Blank takes the vendor's payment terms.
    due_date: '',
    location_id: editing?.location_id ?? '',
    department_id: editing?.department_id ?? '',
    cost_center_id: editing?.cost_center_id ?? '',
    cgst: editing?.cgst_paise ? rupees(editing.cgst_paise) : '',
    sgst: editing?.sgst_paise ? rupees(editing.sgst_paise) : '',
    igst: editing?.igst_paise ? rupees(editing.igst_paise) : '',
    total: editing ? rupees(editing.total_paise) : '',
    itc_eligible: editing?.itc_eligible ?? true,
    is_rcm: editing?.is_rcm ?? false,
    //: "" is the vendor's default; a head name overrides it; "none" is none.
    tds_head: editing?.tds_head ?? '',
  }))
  const [lines, setLines] = useState<DraftLine[]>(() =>
    editing && editing.lines.length
      ? editing.lines.map((l) => ({
          description: l.description,
          quantity: l.quantity ?? '',
          amount: rupees(l.amount_paise),
          category_id: l.category_id ?? '',
          gl_account_id: l.category_id ? '' : l.gl_account_id,
          purchase_order_line_id: l.purchase_order_line_id ?? '',
        }))
      : [BLANK],
  )
  const categories = options?.categories ?? []
  //: The bill just recorded, so its papers can be attached right here
  //: rather than hunted for in the list.
  const [recorded, setRecorded] = useState<Invoice | null>(null)

  const taxable = lines.reduce((sum, l) => sum + toPaise(l.amount), 0)
  const tax = toPaise(form.cgst) + toPaise(form.sgst) + toPaise(form.igst)
  const expected = taxable + tax
  const stated = toPaise(form.total)
  const gap = stated ? stated - expected : 0

  const orderLines = orders.find((o) => o.id === form.purchase_order_id)?.lines ?? []
  const vendor = vendors.find((v) => v.id === form.party_id)

  //: The vendor's GSTIN fills itself in, and says which tax the bill should
  //: carry: the same state as ours is CGST + SGST, another state is IGST.
  const { can } = useAuth()
  const profile = useQuery({
    queryKey: ['org-profile'],
    queryFn: () => api.get<{ state_code: string | null } | null>('/org/profile'),
    enabled: can('org.profile.view'),
  })
  const vendorGstin = vendor?.gstin ?? null
  const theirState = vendorGstin ? vendorGstin.slice(0, 2) : (vendor?.state_code ?? null)
  const ourState = profile.data?.state_code ?? null
  const gstinCaption = !vendor
    ? 'fills in from the vendor'
    : !vendorGstin
      ? 'none on record — unregistered, or add it on the vendor'
      : [
          gstinState(vendorGstin) ?? `state ${theirState}`,
          ourState && theirState
            ? theirState === ourState
              ? 'same state: CGST + SGST'
              : 'another state: IGST'
            : null,
        ]
          .filter(Boolean)
          .join(' · ')

  //: What posting would withhold, from the same engine, as the bill is typed.
  const preview = useQuery({
    queryKey: ['tds-preview', form.party_id, taxable, form.invoice_date, form.tds_head],
    queryFn: () =>
      api.get<TdsPreview>(
        `/procurement/invoices/tds-preview?party_id=${form.party_id}` +
          `&taxable_paise=${taxable}&invoice_date=${form.invoice_date}` +
          (form.tds_head ? `&tds_head=${encodeURIComponent(form.tds_head)}` : ''),
      ),
    enabled: !!form.party_id && taxable > 0,
  })
  const tdsPaise = preview.data?.tds_paise ?? 0

  const create = useMutation({
    mutationFn: () =>
      (editing ? api.put<Invoice> : api.post<Invoice>)(
        editing ? `/procurement/invoices/${editing.id}` : '/procurement/invoices',
        {
          supplier_invoice_no: form.supplier_invoice_no,
          party_id: form.party_id,
          purchase_order_id: form.purchase_order_id || null,
          invoice_date: form.invoice_date,
          due_date: form.due_date || null,
          location_id: form.location_id || null,
          department_id: form.department_id || null,
          cost_center_id: form.cost_center_id || null,
          taxable_paise: taxable,
          cgst_paise: toPaise(form.cgst),
          sgst_paise: toPaise(form.sgst),
          igst_paise: toPaise(form.igst),
          total_paise: stated || expected,
          itc_eligible: form.itc_eligible,
          is_rcm: form.is_rcm,
          tds_head: form.tds_head || null,
          lines: lines.map((l) => ({
            description: l.description,
            quantity: l.quantity || null,
            amount_paise: toPaise(l.amount),
            category_id: l.category_id || null,
            gl_account_id: l.category_id ? null : l.gl_account_id || null,
            purchase_order_line_id: l.purchase_order_line_id || null,
          })),
        },
      ),
    onSuccess: (invoice) => {
      if (editing) {
        onDone()
        onFinished()
        return
      }
      setRecorded(invoice)
      setForm({
        ...form,
        supplier_invoice_no: '',
        cgst: '',
        sgst: '',
        igst: '',
        total: '',
        tds_head: '',
      })
      setLines([BLANK])
      onDone()
    },
  })

  const update = (i: number, patch: Partial<DraftLine>) =>
    setLines(lines.map((l, j) => (i === j ? { ...l, ...patch } : l)))

  if (recorded) {
    return (
      <>
        <Note kind="ok">
          Recorded as <span className="mono">{recorded.reference}</span>. Attach the supplier’s bill
          now, while it is to hand — the approver reads it from here.
        </Note>
        <Attachments
          subjectType="vendor_invoice"
          subjectId={recorded.id}
          kind="invoice"
          label="Attach the supplier’s bill"
        />
        <div className="row" style={{ marginTop: 12 }}>
          <div className="shrink">
            <button className="primary" type="button" onClick={onFinished}>
              Done
            </button>
          </div>
          <div className="shrink">
            <button type="button" onClick={() => setRecorded(null)}>
              Record another
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        create.mutate()
      }}
    >
      <ErrorNote error={create.error} />
      <div className="row">
        <Field label="Vendor">
          <select
            value={form.party_id}
            onChange={(e) => setForm({ ...form, party_id: e.target.value, purchase_order_id: '' })}
            required
          >
            <option value="">Choose…</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vendor's GSTIN" caption={gstinCaption}>
          <input
            className="mono"
            readOnly
            tabIndex={-1}
            aria-label="Vendor's GSTIN"
            value={vendorGstin ?? ''}
            placeholder={vendor ? 'not registered' : '—'}
          />
        </Field>
        <Field label="Supplier's bill number" caption="one per supplier, ever">
          <input
            value={form.supplier_invoice_no}
            onChange={(e) => setForm({ ...form, supplier_invoice_no: e.target.value })}
            required
          />
        </Field>
        <Field label="Bill date">
          <input
            type="date"
            value={form.invoice_date}
            onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
            required
          />
        </Field>
        <Field label="Due date" caption="blank: the vendor’s payment terms">
          <input
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
        </Field>
        <Field label="Against order" caption="blank for rent, utilities and the like">
          <select
            value={form.purchase_order_id}
            onChange={(e) => setForm({ ...form, purchase_order_id: e.target.value })}
          >
            <option value="">None</option>
            {orders
              .filter((o) => !form.party_id || o.party_id === form.party_id)
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.reference}
                </option>
              ))}
          </select>
        </Field>
      </div>

      <div className="row">
        <Field label="Clinic">
          <select
            value={form.location_id}
            onChange={(e) => setForm({ ...form, location_id: e.target.value })}
            required
          >
            <option value="">Choose…</option>
            {(options?.locations ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Department">
          <select
            value={form.department_id}
            onChange={(e) => setForm({ ...form, department_id: e.target.value })}
            required
          >
            <option value="">Choose…</option>
            {(options?.departments ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Cost centre" caption="optional">
          <select
            value={form.cost_center_id}
            onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}
          >
            <option value="">None</option>
            {(options?.cost_centers ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="scroll-x">
        <table style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Description</th>
              <th style={{ width: 240 }}>Expense category</th>
              <th className="num" style={{ width: 100 }}>
                Quantity
              </th>
              <th className="num" style={{ width: 150 }}>
                Amount (₹)
              </th>
              {form.purchase_order_id && <th style={{ width: 200 }}>Order line</th>}
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td>
                  <input
                    value={line.description}
                    onChange={(e) => update(i, { description: e.target.value })}
                    required
                  />
                </td>
                <td>
                  {line.gl_account_id && !line.category_id ? (
                    <SearchSelect
                      aria-label="Expense head"
                      value={line.gl_account_id}
                      onChange={(v) => update(i, { gl_account_id: v })}
                      required
                      placeholder="Choose…"
                      options={accounts.map((a) => ({
                        value: a.id,
                        label: `${a.code} ${a.name}`,
                      }))}
                    />
                  ) : (
                    <SearchSelect
                      aria-label="Expense category"
                      value={line.category_id}
                      onChange={(v) => update(i, { category_id: v, gl_account_id: '' })}
                      required
                      placeholder="Choose…"
                      options={categories.map((c) => ({
                        value: c.id,
                        label: c.note ? `${c.name} (${c.note})` : c.name,
                      }))}
                    />
                  )}
                </td>
                <td>
                  <input
                    className="num"
                    value={line.quantity}
                    onChange={(e) => update(i, { quantity: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="num"
                    value={line.amount}
                    onChange={(e) => update(i, { amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </td>
                {form.purchase_order_id && (
                  <td>
                    <select
                      value={line.purchase_order_line_id}
                      onChange={(e) => update(i, { purchase_order_line_id: e.target.value })}
                    >
                      <option value="">Not on the order</option>
                      {orderLines.map((ol) => (
                        <option key={ol.id} value={ol.id}>
                          {ol.line_no}. {ol.description}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                <td className="num">
                  {lines.length > 1 && (
                    <button
                      type="button"
                      className="small"
                      onClick={() => setLines(lines.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="row">
        <div className="shrink">
          <button type="button" className="small" onClick={() => setLines([...lines, BLANK])}>
            Add a line
          </button>
        </div>
      </div>

      <div className="row">
        <Field label="CGST (₹)">
          <input
            className="num"
            value={form.cgst}
            onChange={(e) => setForm({ ...form, cgst: e.target.value })}
          />
        </Field>
        <Field label="SGST (₹)">
          <input
            className="num"
            value={form.sgst}
            onChange={(e) => setForm({ ...form, sgst: e.target.value })}
          />
        </Field>
        <Field label="IGST (₹)">
          <input
            className="num"
            value={form.igst}
            onChange={(e) => setForm({ ...form, igst: e.target.value })}
          />
        </Field>
        <Field label="Bill total (₹)" caption="as printed, rounded to the rupee">
          <input
            className="num"
            value={form.total}
            onChange={(e) => setForm({ ...form, total: e.target.value })}
            placeholder={String(expected / 100)}
          />
        </Field>
      </div>

      <div className="row">
        <Field
          label="TDS"
          caption={
            vendor?.tds_head
              ? `the vendor’s default is ${headLabel(vendor.tds_head)}`
              : 'the vendor has no TDS section on record'
          }
        >
          <select
            value={form.tds_head}
            onChange={(e) => setForm({ ...form, tds_head: e.target.value })}
          >
            <option value="">
              {vendor?.tds_head
                ? `Vendor’s default — ${headLabel(vendor.tds_head)}`
                : 'Vendor’s default — none'}
            </option>
            {TDS_HEADS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value="none">No TDS on this bill</option>
          </select>
        </Field>
      </div>

      <dl className="appr-facts">
        <div>
          <dt>Taxable + GST</dt>
          <dd>{formatInr(stated || expected)}</dd>
        </div>
        <div>
          <dt>TDS to withhold</dt>
          <dd>
            {formatInr(tdsPaise)}
            <div className="faint">
              {preview.data
                ? preview.data.applies && preview.data.tds_paise > 0
                  ? `${headLabel(preview.data.head)} at ${preview.data.rate_bp / 100}%`
                  : // Why nothing: the reason matters most when the answer is zero.
                    preview.data.reason
                : 'worked out once the vendor and lines are in'}
            </div>
          </dd>
        </div>
        <div>
          <dt>Payable to vendor</dt>
          <dd>
            <strong>{formatInr((stated || expected) - tdsPaise)}</strong>
            <div className="faint">bill total less TDS</div>
          </dd>
        </div>
      </dl>

      <div className="row">
        <label className="appr-check shrink">
          <input
            type="checkbox"
            checked={form.itc_eligible}
            onChange={(e) => setForm({ ...form, itc_eligible: e.target.checked })}
          />
          Input credit claimable
        </label>
        <label className="appr-check shrink">
          <input
            type="checkbox"
            checked={form.is_rcm}
            onChange={(e) => setForm({ ...form, is_rcm: e.target.checked })}
          />
          Reverse charge
        </label>
      </div>

      {stated !== 0 && gap !== 0 && (
        <Note kind={Math.abs(gap) <= 99 ? 'info' : 'error'}>
          {Math.abs(gap) <= 99
            ? `The parts miss the printed total by ${formatInr(Math.abs(gap))}. That is rounding, and it will be posted to a named rounding account.`
            : `The lines and tax come to ${formatInr(expected)} but the bill says ${formatInr(stated)}. That is too much to be rounding — posting will refuse it, so check the figures rather than let it through.`}
        </Note>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <div className="shrink">
          <button className="primary" type="submit" disabled={create.isPending}>
            {editing ? 'Save the correction' : 'Record bill'}
          </button>
        </div>
      </div>
    </form>
  )
}
